import { Api } from "@/config";
import { VuexModule, Module, Mutation, Action } from "vuex-module-decorators";
import ISetting from "@/interfaces/setting";
import Setting from "@/models/setting";
import {
  SET_USER_SETTINGS,
  UPDATE_USER_SETTINGS,
} from "@/store/mutation-types";

@Module({ namespaced: true })
export default class Settings extends VuexModule {
  public availableHttpMethods: Array<string> = [
    "DELETE",
    "GET",
    "HEAD",
    "OPTIONS",
    "POST",
    "PUT",
    "PATCH",
  ];

  public availableSchemes: Array<string> = ["none", "http://", "https://"];

  public availableUserAgents: Array<object> = [
    {
      label: "None",
      value: "none",
      type: "none",
    },
    {
      label: "Urlcheckr/2.0",
      value: "Urlcheckr/2.0",
      type: "none",
    },
    {
      label: "Apple Safari (iOS)",
      value:
        "Mozilla/5.0 (iPhone; CPU iPhone OS 11_0 like Mac OS X) AppleWebKit/604.1.38 (KHTML, like Gecko) Version/11.0 Mobile/15A356 Safari/604.1",
      type: "browser",
    },
    {
      label: "Apple Safari (iPadOS)",
      value:
        "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/13.0 Safari/605.1.15",
      type: "browser",
    },
    {
      label: "Apple Safari (MacOS)",
      value:
        "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_0) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/75.0.3770.100 Safari/537.36",
      type: "browser",
    },
    {
      label: "Bingbot/2.0",
      value:
        "Mozilla/5.0 (compatible; bingbot/2.0; +http://www.bing.com/bingbot.htm)",
      type: "bot",
    },
    {
      label: "Googlebot (Desktop)",
      value:
        "Mozilla/5.0 (compatible; Googlebot/2.1; +http://www.google.com/bot.html)",
      type: "bot",
    },
    {
      label: "Googlebot (Smartphone)",
      value:
        "Mozilla/5.0 (Linux; Android 6.0.1; Nexus 5X Build/MMB29P) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/41.0.2272.96 Mobile Safari/537.36 (compatible; Googlebot/2.1; +http://www.google.com/bot.html)",
      type: "bot",
    },
    {
      label: "Google Chrome",
      value:
        "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/78.0.3904.70 Safari/537.36",
      type: "browser",
    },
    {
      label: "Microsoft Edge",
      value:
        "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML like Gecko) Chrome/51.0.2704.79 Safari/537.36 Edge/14.14931",
      type: "browser",
    },
    {
      label: "Microsoft Internet Explorer 11",
      value: "Mozilla/5.0 (Windows NT 6.3; Trident/7.0; rv:11.0) like Gecko",
      type: "browser",
    },
    {
      label: "Mozilla Firefox",
      value:
        "Mozilla/5.0 (Windows NT 6.1; Win64; x64; rv:47.0) Gecko/20100101 Firefox/47.0",
      type: "browser",
    },
    {
      label: "Opera",
      value:
        "Mozilla/5.0 (Windows NT 6.1; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/28.0.1500.52 Safari/537.36 OPR/15.0.1147.100",
      type: "browser",
    },
  ];

  public maxConcurrentChecksFree = 1;
  public maxConcurrentChecksPremium = 10;

  public userSettings: Array<ISetting> = [];

  //#region Actions
  @Action
  public get(): Promise<void> {
    return new Promise((resolve, reject) => {
      Api.get(`settings`).then(
        (response) => {
          if (response && response.status === 200) {
            let settings: Array<ISetting> = [];

            response.data.forEach((setting: any) => {
              settings.push(new Setting(setting));
            });

            this.context.commit(SET_USER_SETTINGS, settings);
          }

          resolve();
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  @Action
  public update(postModel: ISetting): Promise<void> {
    return new Promise((resolve, reject) => {
      Api.put(`settings`, postModel).then(
        (response) => {
          if (response && response.status === 200) {
            this.context.commit(UPDATE_USER_SETTINGS, response.data);
          }
          resolve();
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  @Action
  public updates(): Promise<void> {
    return new Promise((resolve, reject) => {
      Api.get(`updates`).then(
        (response) => {
          if (response && response.status === 200) {
            resolve();
          }
        },
        (error) => {
          reject(error);
        }
      );
    });
  }
  //#endregion

  //#region Getters
  get followRedirects(): boolean {
    const setting = this.userSettings.find(
      (us) => us.key === "FollowRedirects"
    );

    if (setting) {
      return Boolean(setting.value);
    }

    return false;
  }

  get selectedHttpMethod(): string {
    const setting = this.userSettings.find(
      (us) => us.key === "DefaultHttpMethod"
    );

    if (setting) {
      return setting.value.toString();
    }

    return "";
  }

  get selectedScheme(): string {
    const setting = this.userSettings.find((us) => us.key === "AddScheme");

    if (setting) {
      return setting.value.toString();
    }

    return "";
  }

  get selectedUserAgent(): string {
    const setting = this.userSettings.find((us) => us.key === "UserAgent");

    if (setting) {
      return setting.value.toString();
    }

    return "";
  }

  get validateSslCertificate(): boolean {
    const setting = this.userSettings.find(
      (us) => us.key === "ValidateSslCertificate"
    );

    if (setting) {
      return Boolean(setting.value);
    }

    return false;
  }
  //#endregion

  //#region Mutations
  @Mutation
  public [SET_USER_SETTINGS](value: Array<Setting>) {
    this.userSettings = value;
  }

  //
  // payload:
  // id, key, value
  @Mutation
  public [UPDATE_USER_SETTINGS](payload: { key: string; value: string }) {
    let settingToUpdate = this.userSettings.find(
      (us) => us.key === payload.key
    );
    if (settingToUpdate != null) {
      if (payload.value === "true") {
        settingToUpdate.value = true;
      } else if (payload.value === "false") {
        settingToUpdate.value = false;
      } else {
        settingToUpdate.value = payload.value;
      }
    }
  }
  //#endregion
}
